// Achievement system service for managing achievements

import { buildingLogger } from '../Logger';

import {
  ACHIEVEMENT_DEFINITIONS,
  createInitialAchievementState,
} from './achievements';
import {
  Achievement,
  AchievementType,
  AchievementContext,
  AchievementSystemConfig,
  AchievementUnlockResult,
  AchievementSaveData,
} from './types';

// Achievement system singleton class
export class AchievementSystem {
  private static instance: AchievementSystem | null = null;

  private achievements: Map<AchievementType, Achievement> = new Map();
  private config: AchievementSystemConfig;

  private constructor(
    config: AchievementSystemConfig = {
      showNotifications: true,
      achievementsDisabled: false,
    }
  ) {
    this.config = config;
    this.initializeAchievements();
  }

  // Singleton getter
  public static getInstance(
    config?: AchievementSystemConfig
  ): AchievementSystem {
    if (!AchievementSystem.instance) {
      AchievementSystem.instance = new AchievementSystem(config);
    }
    return AchievementSystem.instance;
  }

  // Initialize achievements with default locked state
  private initializeAchievements(): void {
    const initialAchievements = createInitialAchievementState();
    for (const achievement of initialAchievements) {
      this.achievements.set(achievement.id, achievement);
    }
    buildingLogger.info('Achievement system initialized', {
      totalAchievements: this.achievements.size,
    });
  }

  // Load achievements from save data
  public loadAchievements(saveData: AchievementSaveData): void {
    for (const [achievementId, data] of Object.entries(saveData)) {
      const achievementType = achievementId as AchievementType;
      const achievement = this.achievements.get(achievementType);

      if (achievement && data) {
        achievement.unlocked = data.unlocked;
        if (data.unlockedAt !== undefined) {
          achievement.unlockedAt = data.unlockedAt;
        }
        this.achievements.set(achievementType, achievement);
      }
    }

    const unlockedCount = Array.from(this.achievements.values()).filter(
      a => a.unlocked
    ).length;
    buildingLogger.info('Achievements loaded from save data', {
      unlockedCount,
      totalCount: this.achievements.size,
    });
  }

  // Get achievements as save data format
  public getSaveData(): AchievementSaveData {
    const saveData: AchievementSaveData = {};

    Array.from(this.achievements.entries()).forEach(([type, achievement]) => {
      saveData[type] = {
        unlocked: achievement.unlocked,
        ...(achievement.unlockedAt !== undefined && {
          unlockedAt: achievement.unlockedAt,
        }),
      };
    });

    return saveData;
  }

  // Get all achievements as array
  public getAchievements(): Achievement[] {
    return Array.from(this.achievements.values());
  }

  // Get specific achievement
  public getAchievement(type: AchievementType): Achievement | undefined {
    return this.achievements.get(type);
  }

  // Get unlocked achievements count
  public getUnlockedCount(): number {
    return Array.from(this.achievements.values()).filter(a => a.unlocked)
      .length;
  }

  // Get total achievements count
  public getTotalCount(): number {
    return this.achievements.size;
  }

  // Update system configuration
  public updateConfig(config: Partial<AchievementSystemConfig>): void {
    this.config = { ...this.config, ...config };
  }

  // Check if achievements are disabled
  public areAchievementsDisabled(): boolean {
    return this.config.achievementsDisabled;
  }

  // Unlock an achievement manually (used internally)
  private unlockAchievement(
    type: AchievementType
  ): AchievementUnlockResult | null {
    // Skip if achievements are disabled
    if (this.areAchievementsDisabled()) {
      return null;
    }

    const achievement = this.achievements.get(type);
    if (!achievement) {
      buildingLogger.warn('Attempted to unlock unknown achievement', { type });
      return null;
    }

    const isNewlyUnlocked = !achievement.unlocked;
    if (isNewlyUnlocked) {
      achievement.unlocked = true;
      achievement.unlockedAt = Date.now();
      this.achievements.set(type, achievement);

      buildingLogger.info('Achievement unlocked!', {
        id: achievement.id,
        name: achievement.name,
        description: achievement.description,
      });

      return {
        achievement: { ...achievement },
        isNewlyUnlocked: true,
        message: `🏆 Achievement Unlocked: ${achievement.name}`,
      };
    }

    return {
      achievement: { ...achievement },
      isNewlyUnlocked: false,
    };
  }

  // Check and unlock achievements based on context
  public checkAchievements(
    context: AchievementContext
  ): AchievementUnlockResult[] {
    const results: AchievementUnlockResult[] = [];

    // Skip all checks if achievements are disabled
    if (this.areAchievementsDisabled()) {
      return results;
    }

    // Check each achievement definition
    for (const [type, definition] of Object.entries(ACHIEVEMENT_DEFINITIONS)) {
      const achievementType = type as AchievementType;
      const achievement = this.achievements.get(achievementType);

      // Skip if already unlocked
      if (achievement?.unlocked) {
        continue;
      }

      // Check if conditions are met
      if (definition.check(context)) {
        const result = this.unlockAchievement(achievementType);
        if (result) {
          results.push(result);
        }
      }
    }

    return results;
  }

  // Convenience method to trigger achievement checks
  public triggerGameStart(): AchievementUnlockResult[] {
    return this.checkAchievements({ isNewGame: true });
  }

  public triggerBuildingPlaced(
    buildingType: string,
    position: { x: number; y: number }
  ): AchievementUnlockResult[] {
    return this.checkAchievements({
      buildingPlaced: { buildingType, position },
    });
  }

  // Reset all achievements (for testing or new game+)
  public resetAllAchievements(): void {
    Array.from(this.achievements.values()).forEach(achievement => {
      achievement.unlocked = false;
      delete achievement.unlockedAt;
    });
    buildingLogger.info('All achievements reset');
  }
}
